import {EmailTemplateListDto} from '@common/dto/email-template/list';
import {useMessage} from '@common/hooks/message';
import React from 'react';
import {
    createEmailTemplate,
    deleteEmailTemplate,
    updateEmailTemplates,
    checkQuestionnaires,
    copyEmailTemplate
} from '../services/email-templates';
import {I18n} from 'react-redux-i18n';
import {HttpErrorDto} from '@common/dto/http';
import {getEmailTemplates} from '@common/actions/email-templates';
import isEqual from 'lodash/isEqual';
import {QuestionMessage} from '@common/components/question-message';
import {FieldSelectorOnChangeSingle} from '@common/components/FieldSelector';
import {getModuleFolders} from '@common/actions/folder';
import {FolderType} from '@common/dto/folder/type';
import {getFolderFields} from '@common/actions/formats';
import {FolderContext} from '@common/dto/folder/context';
import {FolderPrimaryListDto} from '@common/dto/folder/primary-list-dto';
import {WarningMessage} from "@common/components/warning-message";

type GetFieldParams = { folderId?: FolderPrimaryListDto["id"]; searchKeyword?: string; context: number };

export const useEmailTemplatesController = () => {
    const [loadingTemplates, setLoadingTemplates] = React.useState<boolean>(true);
    const [templates, setTemplates] = React.useState<Array<EmailTemplateListDto>>([]);
    const [selectedTemplates, setSelectedTemplates] = React.useState<Set<number>>(new Set());
    const [changedTemplates, setChangedTemplates] = React.useState<Set<number>>(new Set());
    const [selectedTemplateId, setSelectedTemplateId] = React.useState<number | undefined>(undefined);
    const [editorState, setEditorState] = React.useState<{ addContent: (text: string) => void } | undefined>();

    const currentEditingTemplate: EmailTemplateListDto | undefined = selectedTemplateId != null
        ? templates.find(t => t.id == selectedTemplateId)
        : undefined

    const openFieldSelector = React.useCallback((editor: { addContent: (text: string) => void }) => {
        setEditorState(editor);
    }, []);

    const closeFieldSelector = React.useCallback(() => {
        setEditorState(undefined);
    }, []);

    const handleFieldSelect = React.useCallback(({field}: FieldSelectorOnChangeSingle) => {
        if (editorState) {
            // editorState.addContent(`{${field.fieldName}}`)
            editorState.addContent(`<span class="simple_field_placeholder" fieldid="${field.fieldId}" systemfield="${field.systemField?1:0}" title="${field.fullPath}">{${field.fieldName}}</span>`);
        }
        setEditorState(undefined);
    }, [editorState]);
    const {
        messageProps,
        showMessage,
        showErrorMessage,
        showInfoMessage,
        closeMessage
    } = useMessage();

    const getAndSetTemplates = React.useCallback(async () => {
        setLoadingTemplates(true);
        try {
            const data = await getEmailTemplates();

            if (selectedTemplateId && selectedTemplateId < 0 && currentEditingTemplate) {
                const selected = data.find(t => t.name === currentEditingTemplate.name || t.id === currentEditingTemplate.id);
                if (selected) {
                    handleTemplatesSelect([selected.id])
                }
            }

            setTemplates(data);
            setChangedTemplates(new Set())
        } catch (err) {
            console.error(err);
            showErrorMessage((err as HttpErrorDto | undefined)?.entity || I18n.t('Failed to load email templates'));
        } finally {
            setLoadingTemplates(false);
        }
    }, [getEmailTemplates, showErrorMessage]);

    React.useEffect(() => {
        getAndSetTemplates();
    }, []);

    const handleTemplateDelete = () => {
        return new Promise<void>((resolve) => {
            showMessage({
                header: I18n.t("Confirmation Message"),
                content: (
                    <QuestionMessage>
                        {I18n.t('Are you sure you want to delete the selected email templates?')}
                    </QuestionMessage>
                ),
                confirmText: I18n.t('Yes'),
                cancelText: I18n.t('No'),
                hideMessageBox: () => {
                    closeMessage();
                    resolve();
                },
                onCustomOk: async () => {
                    try {
                        closeMessage();
                        await doCheckQuestionnaires([...selectedTemplates], resolve);
                    } catch (error) {
                        console.error(error);
                        showErrorMessage(I18n.t('Failed to delete template'));
                    }
                }
            });
        });
    }

    const doCheckQuestionnaires = async (templateIds: Array<number>, resolve: any) => {
        let savedQuestsIds = templateIds.filter(id => id > 0);
        let questionnaires: Map<number, Array<string>> = await checkQuestionnaires(savedQuestsIds);
        if (questionnaires.size == 0) {
            await doDeleteTemplate(templateIds, resolve)
        } else {
            const names: Array<React.ReactNode> = [];
            for (const [id, quests] of questionnaires) {
                names.push(<strong>{templates.find(t => t.id == id)?.name}</strong>)
                names.push(<br/>)
                let n = [...quests]
                if (quests.length > 3) {
                    n = [quests[0]!, quests[1]!, `and ${quests.length - 2} more`]
                }
                names.push(...n.map(q => <div style={{paddingLeft: '10px'}}>{q}</div>))
            }

            const names2 =
                showMessage({
                    header: I18n.t("Confirmation Message"),
                    content: (
                        <WarningMessage>
                            {I18n.t(`Some of the selected email templates are still used in questionnaires:`)}
                            <br/>
                            <br/>
                            <div>
                                {names}
                            </div>
                            <br/>
                            <div>{I18n.t(`Do you want to delete them anyway?`)}</div>
                        </WarningMessage>
                    ),
                    confirmText: I18n.t('Yes'),
                    cancelText: I18n.t('No'),
                    size: 'medium',
                    hideMessageBox: () => {
                        closeMessage();
                        resolve();
                    },
                    onCustomOk: async () => {
                        try {
                            closeMessage();
                            await doDeleteTemplate(templateIds, resolve);
                        } catch (error) {
                            console.error(error);
                            showErrorMessage(I18n.t('Failed to delete template'));
                        }
                    }
                });
        }
    }

    const doDeleteTemplate = async (templateIds: Array<number>, resolve: any) => {
        try {
            await deleteEmailTemplate(templateIds.filter(id => id > 0));
            setSelectedTemplates(new Set());
            setSelectedTemplateId(undefined);

            setTemplates((prev) => prev.filter((t) => !templateIds.includes(t.id)));
            setChangedTemplates((prev) => {
                const updated = new Set(prev);
                templateIds.forEach((id) => updated.delete(id));
                return updated;
            });
        } finally {
            resolve();
        }
    }

    const handleTemplateSave = async () => {
        try {
            const toSave = templates.filter(t => changedTemplates.has(t.id));
            let duplicatedName: string | undefined;
            let emptyName = false;
            toSave.forEach(t => {
                const duplicates = templates.filter(d => d.name === t.name);
                if (duplicates.length > 1) {
                    duplicatedName = t.name;
                    return;
                }
                if (t.name === undefined || t.name.trim().length == 0 ||
                    t.subject === undefined || t.subject.trim().length == 0) {
                    emptyName = true;
                    return;
                }
                if (t.id < 0) t.id = 0
            })
            if (duplicatedName) {
                showErrorMessage(`${I18n.t('Template with name')} ${duplicatedName} ${I18n.t('already exists')}`)
                return;
            }
            if (emptyName) {
                showErrorMessage(`${I18n.t('Template name and subject cannot be blank')}`)
                return;
            }
            await updateEmailTemplates(toSave);
            setChangedTemplates(new Set())
            await getAndSetTemplates();
        } catch (error) {
            console.error(error);
            showErrorMessage(I18n.t('Failed to update template'));
        }
    };

    const handleTemplateLocalChange = (template: EmailTemplateListDto) => {
        setChangedTemplates((prev) => {
            return new Set(prev).add(template.id);
        });
        setTemplates((prev) =>
            prev.map((t) => (t.id === selectedTemplateId ? template : t))
        );
    };

    const handleTemplateCopy = async () => {
        if (currentEditingTemplate) {
            const templateCopy: EmailTemplateListDto = {
                ...currentEditingTemplate,
                id: -Date.now(),
                name: currentEditingTemplate.name + " (copy)"
            }
            setChangedTemplates((prev) => {
                return new Set(prev).add(templateCopy.id);
            });
            setTemplates(prev => [...prev, templateCopy]);
            handleTemplatesSelect([templateCopy.id])
        }
    };

    const handleTemplateCreate = async () => {
        const newTemplate: EmailTemplateListDto = {id: -Date.now(), name: "New Template", body: "", locked: false, subject: ""}
        setChangedTemplates((prev) => {
            return new Set(prev).add(newTemplate.id);
        });
        setTemplates(prev => [...prev, newTemplate]);
        handleTemplatesSelect([newTemplate.id])
    };

    const handleTemplatesSelect = React.useCallback((templates: Array<number>) => {
        if (templates.length === 1) {
            setSelectedTemplateId(templates[0]);
        } else {
            setSelectedTemplateId(undefined);
        }
        setSelectedTemplates(new Set(templates))
    }, []);

    const templateHasChanged = React.useMemo(() => {
        return changedTemplates.size > 0;
    }, [changedTemplates]);

    const handleGetFolders = React.useCallback(
        () =>
            getModuleFolders({
                folderTypeId: FolderType.individual,
                systemFolderConfig: {
                    include: true,
                },
                setUp: {
                    rootFolderName: I18n.t("Individual Data Folders"),
                    rootFolderImgUrl: "gif/df_individual.gif",
                },
            }),
        []
    );


    const handleGetFields = React.useCallback(({searchKeyword, folderId}: GetFieldParams) => {
        return getFolderFields({
            searchQuery: searchKeyword,
            folderId,
            contextId: FolderContext.individual,
            includeTables: true,
        }).then((fields) => fields.map((field) => ({...field, fieldName: field.name, fieldId: field.id})));
    }, []);

    const showCloseTabConfirmationMessage = () => {
        showInfoMessage(
            I18n.t("Are you sure you want to close tab without saving?"),
            {
                confirmText: I18n.t("Yes"),
                cancelText: I18n.t("No"),
                hideMessageBox: () => {
                    closeMessage();
                },
                onCustomOk: () => {
                    closeMessage();
                    (window as any).closeEmailTemplates();
                },
            }
        );
    };

    React.useEffect(() => {
        (window as any).onCloseEmailTemplates = () => {
            return new Promise<{ hasUnsavedChanges: boolean }>((resolve) => {
                const hasUnsavedChanges = changedTemplates.size > 0;

                if (hasUnsavedChanges) {
                    showCloseTabConfirmationMessage();
                }

                resolve({
                    hasUnsavedChanges,
                });
            });
        };
    }, [changedTemplates]);

    return {
        templateHasChanged,
        loadingTemplates,
        templates,
        handleTemplateLocalChange,
        currentEditingTemplate,
        closeMessage,
        messageProps,
        selectedTemplates,
        handleTemplateDelete,
        handleTemplateSave,
        handleTemplateCopy,
        handleTemplateCreate,
        handleTemplatesSelect,
        editorState,
        openFieldSelector,
        closeFieldSelector,
        handleFieldSelect,
        handleGetFolders,
        handleGetFields
    }
};